import React from 'react';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface MetricTooltipProps {
  children: React.ReactNode;
  explanation: string;
  className?: string;
}

/**
 * MetricTooltip — Adds a discrete (i) icon next to a metric label.
 * Tooltip appears on hover with a short explanation (max 3 lines).
 */
const MetricTooltip: React.FC<MetricTooltipProps> = ({ children, explanation, className }) => (
  <span className={`inline-flex items-center gap-1 ${className || ''}`}>
    {children}
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground cursor-help flex-shrink-0" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[280px] text-xs leading-relaxed">
        <p>{explanation}</p>
      </TooltipContent>
    </Tooltip>
  </span>
);

// ─── Predefined metric explanations ──────────────────────────────────────
export const METRIC_EXPLANATIONS = {
  // ─── Score & Risk ──────────────────────────────────────────────────
  scoreCriticidade: 'Combina probabilidade de atraso (30%), estouro orçamentário (20%), performance operacional (25%), tendência temporal (15%) e reincidência (10%).',
  probAtraso: 'Estimativa da chance de atraso no cronograma, calculada por simulação Monte Carlo com 5.000 cenários.',
  probEstouro: 'Chance do custo final exceder o valor contratado, baseada em simulação Monte Carlo.',
  monteCarlo: 'Técnica estatística que projeta milhares de cenários aleatórios para estimar riscos de prazo e custo.',
  volatilidade: 'Variação do nível de risco ao longo do tempo. Alta volatilidade indica instabilidade no desempenho do contrato.',

  // ─── EVM Performance Indices ───────────────────────────────────────
  spi: 'SPI = EV / PV. Mede o desempenho de prazo comparando progresso físico executado com o planejado. Valor < 1.0 indica atraso.',
  cpi: 'CPI = EV / AC. Mede a eficiência de custo comparando valor físico executado com custo financeiro aprovado. Valor < 1.0 indica estouro.',
  tcpi: 'TCPI = (BAC − EV) / (BAC − AC). Índice de desempenho necessário para concluir dentro do orçamento. Valor > 1.0 exige eficiência acima do histórico.',

  // ─── EVM Absolute Values ──────────────────────────────────────────
  ev: 'Earned Value — valor físico reconhecido com base nas execuções validadas e medições aprovadas.',
  ac: 'Actual Cost — custo financeiro real baseado no valor líquido dos boletins aprovados para faturamento.',
  pv: 'Planned Value — valor planejado para execução até a data atual, conforme cronograma baseline.',
  bac: 'Budget at Completion — orçamento total aprovado do contrato (valor global vigente).',
  eac: 'Estimate at Completion — projeção do custo total baseada no desempenho atual (BAC / CPI).',
  etc: 'Estimate to Complete — custo estimado para concluir o restante do trabalho (EAC − AC).',
  vac: 'Variance at Completion — diferença entre orçamento e projeção final (BAC − EAC). Valor negativo indica estouro projetado.',

  // ─── Progress ─────────────────────────────────────────────────────
  progressoFisico: 'Baseado em execuções de campo validadas. Representa o avanço físico real da obra, independentemente da medição ou faturamento.',
  progressoFinanceiro: 'Baseado em medições aprovadas para faturamento. Representa o valor financeiro reconhecido do contrato.',
} as const;

export default MetricTooltip;
